import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, BarChart3, Download, Globe, ExternalLink, MapPin } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { adminApi } from '../context/AdminAuthContext';

const T = { vert: '#1e4a1a', or: '#c4a76c', textPrimary: '#1a1a1a', textSecondary: '#6b7280', textMuted: '#9ca3af', border: '#e8e5e0' };

const SOURCE_LABELS = { direct: 'Acces direct', search: 'Moteur de recherche', social: 'Reseaux sociaux', referral: 'Lien externe' };
const SOURCE_COLORS = { direct: '#c4a76c', search: '#16a34a', social: '#2563eb', referral: '#7c3aed' };

const COUNTRY_FLAGS = {
  'France': 'FR', 'Cote d\'Ivoire': 'CI', "Cote d'Ivoire": 'CI', 'United States': 'US', 'Germany': 'DE',
  'Belgium': 'BE', 'Canada': 'CA', 'United Kingdom': 'GB', 'Switzerland': 'CH', 'Senegal': 'SN',
  'Morocco': 'MA', 'Cameroon': 'CM', 'Nigeria': 'NG', 'Ghana': 'GH', 'Mali': 'ML',
};

const getFlag = (location) => {
  for (const [country, code] of Object.entries(COUNTRY_FLAGS)) {
    if (location?.includes(country)) {
      const codePoints = [...code].map(c => 0x1F1E6 + c.charCodeAt(0) - 65);
      return String.fromCodePoint(...codePoints);
    }
  }
  return String.fromCodePoint(0x1F30D);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-4 py-3 bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <p className="text-[11px] mb-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>{label}</p>
      <p className="text-sm font-bold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>
        {payload[0].value} <span className="text-[11px] font-normal" style={{ color: T.or }}>visiteurs</span>
      </p>
    </div>
  );
};

const AnalyticsPanel = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setData(await adminApi.get('/api/admin/analytics'));
      } catch (err) { console.error('Analytics load failed:', err); }
      setLoading(false);
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: T.border, borderTopColor: T.vert }} />
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-center py-10" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Impossible de charger les analytics</p>;
  }

  const totalSourceCount = data.traffic_sources?.reduce((s, t) => s + t.count, 0) || 1;
  const chartData = (data.daily || []).map(d => ({
    date: new Date(d.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
    visitors: d.unique_count || 0,
  }));

  const kpis = [
    { icon: Users, label: "Aujourd'hui", value: data.visits.today.unique, sub: `${data.visits.today.total} visites`, accent: false },
    { icon: TrendingUp, label: 'Ce mois', value: data.visits.month.unique, sub: `${data.visits.month.total} visites`, accent: false },
    { icon: BarChart3, label: '7 jours', value: data.visits.week.unique, sub: `${data.visits.week.total} visites`, accent: false },
    { icon: Download, label: 'Leads brochure', value: data.brochure_leads, sub: 'telechargements', accent: true },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ icon: Icon, label, value, sub, accent }) => (
          <div key={label} className="rounded-2xl p-5 bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: accent ? 'rgba(196,167,108,0.1)' : 'rgba(30,74,26,0.06)' }}>
                <Icon className="w-4 h-4" style={{ color: accent ? T.or : T.vert }} />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>{label}</span>
            </div>
            <p className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>{value}</p>
            <p className="text-[11px] mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>{sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl p-6 bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid="daily-chart">
          <h3 className="text-[13px] font-semibold mb-6 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
            <BarChart3 className="w-4 h-4" style={{ color: T.vert }} /> Visites &mdash; 7 derniers jours
          </h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="greenGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={T.vert} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={T.vert} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: T.textMuted, fontFamily: "'Montserrat', sans-serif" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: T.textMuted }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="visitors" stroke={T.vert} strokeWidth={2} fill="url(#greenGrad)" dot={{ fill: T.vert, r: 3, strokeWidth: 0 }} activeDot={{ r: 5, fill: T.vert, stroke: '#fff', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-center py-8" style={{ color: T.textMuted }}>Aucune donnee</p>
          )}
        </div>

        <div className="rounded-2xl p-6 bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid="traffic-sources">
          <h3 className="text-[13px] font-semibold mb-6 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
            <ExternalLink className="w-4 h-4" style={{ color: T.vert }} /> Sources de trafic
          </h3>
          <div className="space-y-4">
            {(data.traffic_sources || []).map((s, i) => {
              const pct = Math.round((s.count / totalSourceCount) * 100);
              const color = SOURCE_COLORS[s.source] || T.textMuted;
              return (
                <div key={`source-${s.source}`}>
                  <div className="flex justify-between text-[12px] mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    <span className="font-medium" style={{ color: T.textPrimary }}>{SOURCE_LABELS[s.source] || s.source}</span>
                    <span style={{ color: T.textMuted }}>{s.count} ({pct}%)</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#f0ede8' }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
            {(!data.traffic_sources || data.traffic_sources.length === 0) && (
              <p className="text-xs text-center py-4" style={{ color: T.textMuted }}>Aucune donnee</p>
            )}
          </div>
        </div>
      </div>

      {!compact && (
        <div className="rounded-2xl p-6 bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid="geo-locations">
          <h3 className="text-[13px] font-semibold mb-5 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
            <Globe className="w-4 h-4" style={{ color: T.vert }} /> Provenance geographique
          </h3>
          <div>
            {(data.top_locations || []).map((loc, i) => (
              <div key={`loc-${loc.location}`} className="flex items-center justify-between py-3" style={{ borderBottom: i < data.top_locations.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                <div className="flex items-center gap-3">
                  <span className="text-lg">{getFlag(loc.location)}</span>
                  <span className="text-[13px] flex items-center gap-1.5" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
                    <MapPin className="w-3 h-3" style={{ color: T.textMuted }} /> {loc.location}
                  </span>
                </div>
                <span className="text-[14px] font-bold" style={{ fontFamily: "'Playfair Display', serif", color: T.vert }}>{loc.count}</span>
              </div>
            ))}
            {(!data.top_locations || data.top_locations.length === 0) && (
              <p className="text-xs text-center py-6" style={{ color: T.textMuted }}>Aucune donnee geographique</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPanel;
